import { Tooltip, Typography, styled } from "@material-ui/core";
import FeedbackOutlinedIcon from '@material-ui/icons/FeedbackOutlined';
import {
  AttributesMixin,
  define,
  Events,
  memberOf,
  Store,
  type
} from "@type-r/models";
import { useEvent, useIO, useLocalStorage, useModel } from "@type-r/react";
import {
  AddFilter,
  asCurrency,
  asTimelessDate,
  CompactTabs,
  FiltersList,
  Flex,
  Loading,
  Page,
  SelectSortBy,
  SmallTile,
  Tab,
  TilesList,
  useLinkedQueryParam
} from "lqx-react";
import { Menu, MenuItem } from "lqx-react";
import * as React from "react";
import { useMemo } from "react";
import { CompanyModel, globalStore } from "api";
import { InvoiceModel } from "api/invoice";
import {
  InmatchConversation,
  InvoiceMatch,
  Payment,
  TransactionCurrentType,
  UnreconciledPaymentFilters
} from "src/app/pages/inmatch/api";
import { ArMoveDialog } from "../../bank-transactions/nonAr/arMoveDialog";
import { MatchLabel, SmallChip } from "../../../../../../common/inmatch";
import { PaymentDetails } from "./payment-details";
import { FiscalCalendar, createFiscalCalendarMap } from "../../api/fiscal-calendar";
import ErrorIcon from '@material-ui/icons/Error';
import { PlatformCompanyConfig } from "../../api/common";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { getPlatformCode } from "src/app/pages/digitization/api/endpoint";

interface State extends AttributesMixin<typeof State> {}
@define
class State extends Store {
  static attributes = {
    payments: type([Payment]).watcher("updateIndex"),
    paymentToMove: type(Payment).null,
    paymentMoveTo: type(String).null,
    companies: [CompanyModel],
    selected: memberOf("payments", Payment),
    category: "all",
    filter: type(UnreconciledPaymentFilters),
    sortBy: type({
      name: "statementDate",
      isAsc: false
    }).watcher("sortPayments"),
    triggerIO : false,
    customerConfigs: [PlatformCompanyConfig],
  };

  getPayment = (paymentId: Number) => this.payments.get(paymentId.toString());

  initialize() {
    this.sortPayments();

    this.listenTo(Events, {
      "summary:refresh": () => {
        if (!this.selected.unreconciledAmount) {
          this.selected = null;
        }

        this.fetch();
      },

      "InvoiceTile:selectPayment": paymentId => {
        this.selected = this.payments.get(paymentId);;
      }
    });
  }

  sortPayments() {
    this.payments.comparator = Payment.comparator(
      this.sortBy.name as any,
      this.sortBy.isAsc
    );
    this.payments.sort();
  }

  updateIndex() {

    // Clean up selected if it was removed.
    if (!this.payments.get(this.selected)) {
      this.selected = null;
    }
  }

  async fetch(paymentId = null, customerExternalIdParam = null) {
    this.companies = globalStore.companies;

    await Promise.all([
      this.payments.fetch(),
    ]);

    if (!this.selected && !paymentId) {
      this.selected = this.payments.first();
    } else if (paymentId) {
      this.selected =
        this.payments.find(x => x.id == paymentId) || this.payments.first();
    }

    if (customerExternalIdParam) {
      // Reset local storage filters when query param filter is applied i.e.
      // inmatch/matching/unreconciled?customerExternalId=CX0000061-870
      
      this.filter = new UnreconciledPaymentFilters();
    }

    if (this.payments) {
      this.filter.initializeOptions(this.payments);
    }

    return this;
  }
}

const sortPaymentsOptions = {
  statementDate: "Statement Date",
  valueDate: "Value Date",
  customerName: "Customer Name",
  supplierName: "Supplier Name",
  amount : 'Payment Amount'
  //paymentId : 'Payment Id'
};

// Orders management page
export const PaymentsPage = ({}: {}) => {
  const state = useModel(State);
  useLocalStorage('settings.inmatch.payment.unreconciled.filter', {f: state.$.filter});

  const $paymentId = useLinkedQueryParam("paymentId");
  const $customerExternalIdParam = useLinkedQueryParam("customerExternalId");

  const isReady = useIO(async () => state.fetch($paymentId.value, $customerExternalIdParam.value));

  const areDetailsReady = useIO(async () => {
    if (state.selected) {
      $paymentId.set(state.selected.id);
      await Promise.all([
        state.selected.fetch(),
        state.selected.remittanceAdvices.fetch(),
        state.customerConfigs.fetch({params: {platformCompanyId: state.selected.customerPlatformCompanyId}}),
        state.selected.customerConfigs.fetch({params: { 
          platformCompanyId: state.selected.customerPlatformCompanyId,
          platformCode: getPlatformCode(),
        }}),
      ]);
      await state.selected.uploads.fetch()
        .catch(e => console.error(e));
    }
  }, [state.selected, state.triggerIO]);

  const prepareSuccessConditions = () => {
    
    let supplierPlatformCompanyIdsWithSelectedName = [];
      if (state.filter.supplierName.selected && state.filter.supplierName.selected.length) {
        supplierPlatformCompanyIdsWithSelectedName = globalStore.platformCompanies
          .filter(company => state.filter.supplierName.selected.includes(company.name))
          .map(company => company.id);
      }

      
      let customerPlatformCompanyIdsWithSelectedName = [];
      if (state.filter.customerName.selected && state.filter.customerName.selected.length) {
        customerPlatformCompanyIdsWithSelectedName = globalStore.platformCompanies
          .filter(company => state.filter.customerName.selected.includes(company.name))
          .map(company => company.id);
      }

    return { supplierPlatformCompanyIdsWithSelectedName, customerPlatformCompanyIdsWithSelectedName };
  }

  const filtered = useMemo(
    () => {
      const validConditions = prepareSuccessConditions();

      return state.payments.filter(x => x.filter(state.category, state.filter, validConditions.supplierPlatformCompanyIdsWithSelectedName, 
        validConditions.customerPlatformCompanyIdsWithSelectedName))
    },
    [
      state.payments["_changeToken"],
      state.filter["_changeToken"],
      state.category
    ]
  );

  const counts = useMemo(
    () => {
      const validConditions = prepareSuccessConditions();
      return {
        matched: state.payments.filter(x => x.filter('matched', state.filter, validConditions.supplierPlatformCompanyIdsWithSelectedName,
          validConditions.customerPlatformCompanyIdsWithSelectedName)).length,
        unmatched: state.payments.filter(x => x.filter('unmatched', state.filter, validConditions.supplierPlatformCompanyIdsWithSelectedName, 
          validConditions.customerPlatformCompanyIdsWithSelectedName)).length
      }},
    [
      state.payments["_changeToken"],
      state.filter["_changeToken"]
    ]
  );

  const movePayment = (payment: Payment, moveTo: TransactionCurrentType) => {
    state.$.paymentToMove.set(payment);
    state.$.paymentMoveTo.set(moveTo);
  };
  const paymentMoved = (paymentId: number) => {
    state.payments.remove(paymentId);
  };

  useEvent(Events, "selectedPayment:refresh", () => {
    state.$.triggerIO.set(!state.triggerIO);
  });

  return isReady ? (
    <Page
      menuWidth={530}
      maxWidth="xl"
      left={
        <>
          <CompactTabs label="Receivables" $value={state.$.category}>
            <Tab value="all">All ({counts.matched + counts.unmatched})</Tab>
            <Tab value="matched">Matched ({counts.matched})</Tab>
            <Tab value="unmatched">Unmatched ({counts.unmatched})</Tab>
          </CompactTabs>
          <FiltersList filter={state.filter} />
          <AddFilter
            filter={state.filter}
            filterAllPlaceholder="Filter by unreconciled payment amount, bank account, statement date, customer name, supplier name, days due"
          />
          <Flex row>
            <SelectSortBy
              $attr={state.sortBy.$.name}
              $asc={state.sortBy.$.isAsc}
              options={sortPaymentsOptions}
            />
          </Flex>

          {filtered.length ? (
            <TilesList
              items={filtered}
              itemSize={64}
              renderer={PaymentTile(state.$.selected, movePayment)}
              selectedItem={state.selected}
            />
          ) : (
            <Flex center stretch>
              <Typography variant="h4">No results found.</Typography>
            </Flex>
          )}
        </>
      }
    >
      {!areDetailsReady ? (
        <Loading />
      ) : areDetailsReady && state.selected && state.selected.matches && state.selected.paymentDetail ? (
        <PaymentDetails payment={state.selected} getPayment={state.getPayment} allPayments={state.payments} platformCompanyConfigs={state.customerConfigs}/>
      ) : (
        <Flex center stretch>
          Select the payment
        </Flex>
      )}

      {state.paymentToMove ? (
        <ArMoveDialog
          $payment={ state.$.paymentToMove }
          moveTo={ state.paymentMoveTo as TransactionCurrentType }
          paymentMoved={ paymentMoved }
        />
      ) : (
        undefined
      )}
    </Page>
  ) : (
    <Loading />
  );
};

const PaymentTile = ($selected, movePayment) => ({ data, style, index }) => {
  const payment: Payment = data.items[index];

  const recommendationMatchesCount = payment.matchesAggregation && payment.matchesAggregation.recommendationsCount;

    const isRecommendationActive = payment.recommendations.length && payment.recommendations.every(recom => recom.matches.every(m => !m.isActive));

  const unfundedInvoiceCount = payment.matchesAggregation && payment.matchesAggregation
  
  const chipStyle = {
    color: 'white',
    textColor: 'black',
    border: true
  }
  
  return (
    <div style={style}>
      <SmallTile
        color={payment.color}
        isSelected={$selected.value === payment}
        onClick={e => {
          $selected.set(payment);
        }}
        footer={
          <>
            <div>{payment.customerName}</div>
            <Flex grow />

            { null }
            {payment.matchesAggregation && payment.matchesAggregation.remittanceInfoCount ? <MatchLabel type="remittance_info" count={payment.matchesAggregation.remittanceInfoCount}  chipStyle={chipStyle}/> : null}
            {payment.matchesAggregation && payment.matchesAggregation.remittanceAdviceCount ? <MatchLabel type="remittance_advice" count={payment.matchesAggregation.remittanceAdviceCount} chipStyle={chipStyle} unfundedInvoiceCount={payment.matchesAggregation.unfundedCount}/> : null}
            {payment.matchesAggregation && payment.matchesAggregation.manualCount ? <MatchLabel type="manual" count={payment.matchesAggregation.manualCount} chipStyle={chipStyle}/> : null}
            {(payment.matchesAggregation && (payment.matchesAggregation.heuristicCount || payment.matchesAggregation.greedyCount)) ? 
              <MatchLabel type="heuristic"  count={(payment.matchesAggregation.heuristicCount || 0) + (payment.matchesAggregation.greedyCount || 0)} chipStyle={chipStyle}/> : null}
            {recommendationMatchesCount > 1 || isRecommendationActive ? <MatchLabel type="heuristic" label='Inm Recommendations' chipStyle={chipStyle}/> : null}
            {payment.unreconciledAmount > 1000_000_000  ? <Tooltip title='payment is larger than the current threshold to run the algo'><ErrorIcon style={{color: '#007bff'}}/></Tooltip> : null}
          </>
        }
        action={
          Can(UserEntitlementCatalogs.BankEntryWrite) &&
          <Menu>
            <MenuItem onSelect={() => movePayment(payment, 'nonar')}>
              Move to Non-AR
            </MenuItem>
            <MenuItem onSelect={() => movePayment(payment, 'uac')}>
              Assign as UAC
            </MenuItem>
          </Menu>
        }
      >
        {asCurrency(payment.displayAmount, payment.currency)}
        <Flex grow />
        {payment.age > 2 ? <SmallChip>{payment.age} days</SmallChip> : null}
        {asTimelessDate(payment.valueDate)}
      </SmallTile>
    </div>
  );
};

export const Label = styled('span')({
  marginLeft: '2px',
  fontSize: 14
});